import {
  formatSettlementCurrency,
  paymentContextLabel,
  platformFeeForPayment,
  customerPaidAmount,
  type PlatformPaymentSettlement,
} from "@/lib/platform-settlements";
import { format } from "date-fns";

type Props = {
  payments: PlatformPaymentSettlement[];
  compact?: boolean;
};

export function PaymentSettlementTable({ payments, compact }: Props) {
  if (payments.length === 0) {
    return <p className="text-sm text-muted-foreground py-2">No payments in this batch.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/40">
            <th className="text-left p-2 font-medium">Paid</th>
            <th className="text-left p-2 font-medium">Type</th>
            {!compact && <th className="text-left p-2 font-medium">Customer</th>}
            <th className="text-right p-2 font-medium">Service</th>
            <th className="text-right p-2 font-medium">Platform fee</th>
            <th className="text-right p-2 font-medium">To business</th>
            {!compact && <th className="text-left p-2 font-medium">Ref</th>}
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p.id} className="border-b last:border-0">
              <td className="p-2 text-muted-foreground whitespace-nowrap">
                {p.paid_at ? format(new Date(p.paid_at), "MMM d, h:mm a") : "—"}
              </td>
              <td className="p-2">{paymentContextLabel(p.context)}</td>
              {!compact && (
                <td className="p-2 text-muted-foreground max-w-[140px] truncate" title={p.payer_email}>
                  {p.payer_email}
                </td>
              )}
              <td className="p-2 text-right">{formatSettlementCurrency(Number(p.gross_amount), p.currency)}</td>
              <td className="p-2 text-right">
                {formatSettlementCurrency(platformFeeForPayment(p), p.currency)}
              </td>
              <td className="p-2 text-right font-medium">
                {formatSettlementCurrency(Number(p.net_amount), p.currency)}
              </td>
              {!compact && (
                <td className="p-2 text-xs text-muted-foreground font-mono truncate max-w-[120px]">
                  {p.provider_reference || "—"}
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-muted/30 font-medium">
            <td colSpan={compact ? 2 : 3} className="p-2">
              {payments.length} payment{payments.length === 1 ? "" : "s"}
            </td>
            <td className="p-2 text-right">
              {formatSettlementCurrency(
                payments.reduce((s, p) => s + Number(p.gross_amount || 0), 0),
                payments[0]?.currency,
              )}
            </td>
            <td className="p-2 text-right">
              {formatSettlementCurrency(
                payments.reduce((s, p) => s + platformFeeForPayment(p), 0),
                payments[0]?.currency,
              )}
            </td>
            <td className="p-2 text-right">
              {formatSettlementCurrency(
                payments.reduce((s, p) => s + Number(p.net_amount || 0), 0),
                payments[0]?.currency,
              )}
            </td>
            {!compact && <td />}
          </tr>
          {!compact && (
            <tr className="text-xs text-muted-foreground">
              <td colSpan={7} className="p-2">
                Customer paid total:{" "}
                {formatSettlementCurrency(
                  payments.reduce((s, p) => s + customerPaidAmount(p), 0),
                  payments[0]?.currency,
                )}
              </td>
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );
}
